import { formatBRDateTime } from '@control-center/shared';
import { Check, FileWarning } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { useDivergenceAlerts, useResolveDivergenceAlert } from '../../hooks/use-dashboard';

/** Story 6.8 — Widget divergências não resolvidas (top 5 + link Ver todos). */
export function DivergenceWidget() {
  const query = useDivergenceAlerts(false);
  const resolve = useResolveDivergenceAlert();

  if (query.isLoading) {
    return <div className="h-64 animate-pulse rounded-lg border border-line bg-panel-2" />;
  }

  if (query.isError) {
    return (
      <Card title="Divergências">
        <div className="p-4 text-sm text-bad">Erro ao carregar.</div>
      </Card>
    );
  }

  const items = query.data?.data.items ?? [];
  const total = query.data?.data.total ?? 0;

  if (total === 0) {
    return (
      <Card title="Divergências">
        <div className="py-8 text-center text-sm text-muted">✅ Nenhuma divergência pendente</div>
      </Card>
    );
  }

  const top5 = items.slice(0, 5);

  const handleResolve = (id: string) => {
    const notes = window.prompt('Nota da resolução (opcional, min 3 chars):') ?? undefined;
    resolve.mutate(
      { id, notes: notes && notes.length >= 3 ? notes : undefined },
      {
        onSuccess: () => toast.success('Divergência resolvida'),
        onError: () => toast.error('Falha ao resolver — tente novamente'),
      },
    );
  };

  return (
    <Card title={`Divergências (${total} pendentes)`}>
      <ul className="divide-y divide-line">
        {top5.map((alert) => (
          <li key={alert.id} className="flex items-start gap-3 px-4 py-3">
            <FileWarning className="mt-0.5 h-4 w-4 shrink-0 text-warn" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-sm font-medium text-ink">
                <span className="truncate">{formatType(alert.type)}</span>
                {alert.client_name && (
                  <span className="truncate font-normal text-muted">· {alert.client_name}</span>
                )}
              </div>
              <div className="mt-0.5 text-xs text-muted-2">
                {formatBRDateTime(new Date(alert.detected_at))}
              </div>
            </div>
            <button
              type="button"
              onClick={() => handleResolve(alert.id)}
              disabled={resolve.isPending}
              className="inline-flex items-center gap-1 rounded-md border border-line bg-panel-2 px-2 py-1 text-xs font-medium text-ink-2 transition-opacity hover:opacity-80 disabled:opacity-50"
            >
              <Check className="h-3 w-3" />
              Resolver
            </button>
          </li>
        ))}
      </ul>
      {total > 5 && (
        <div className="border-t border-line px-4 py-2 text-right">
          <Link
            to="/admin/divergence-alerts"
            className="text-xs font-medium text-brand hover:underline"
          >
            Ver todas ({total}) →
          </Link>
        </div>
      )}
    </Card>
  );
}

function formatType(type: string): string {
  const map: Record<string, string> = {
    communication_failed: 'Comunicação falhou',
    webhook_silent: 'Webhook silencioso',
    chargeback: 'Chargeback',
    client_long_overdue: 'Cliente muito vencido',
    whatsapp_from_unknown: 'WhatsApp desconhecido',
    email_invalid: 'Email inválido',
  };
  return map[type] ?? type;
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-lg border border-line bg-panel">
      <div className="border-b border-line px-4 py-3">
        <h3 className="text-sm font-medium text-ink">{title}</h3>
      </div>
      {children}
    </div>
  );
}
